// React Query hooks for the VPN. Every key starts with `vpnKeys.all`, so a
// mutation invalidates what it touches by prefix, and a later page adds keys
// beside these rather than inventing its own.
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useActiveContextReady } from '@/hooks/useActiveContextReady';
import { useAuth } from '@/hooks/useAuth';
import { apiErrorMessage } from '@/utils/apiError';
import {
  acknowledgeApp,
  bindApp,
  disconnectApp,
  extendSite,
  fetchAppBinding,
  fetchAppBindings,
  fetchAppDownloads,
  fetchSites,
  turnSiteOff,
  turnSiteOn,
  endSession,
  fetchDnsDomains,
  fetchGatewayVpn,
  createPolicy,
  deletePolicy,
  fetchMyDevices,
  fetchPathSettings,
  fetchPolicies,
  fetchReachable,
  fetchRegionVpn,
  fetchRegionVpns,
  fetchSessions,
  revokeDevice,
  setDnsDomains,
  setPathSetting,
  setRegionVpn,
  updatePolicy,
  type DnsDomains,
  type PathSetting,
  type VpnPolicyInput,
  type SetRegionVpn,
  type VpnSessionQuery,
  type AppBinding,
} from './api';

/** Live data refreshes this often while it is on screen. */
export const LIVE_REFRESH_MS = 15_000;

export const vpnKeys = {
  all: ['vpn'] as const,
  sessions: () => [...vpnKeys.all, 'sessions'] as const,
  sessionPage: (query: VpnSessionQuery) => [...vpnKeys.sessions(), query] as const,
  regions: () => [...vpnKeys.all, 'regions'] as const,
  region: (regionId: string) => [...vpnKeys.regions(), regionId] as const,
  gateway: (gatewayId: string) => [...vpnKeys.all, 'gateway', gatewayId] as const,
  dnsDomains: () => [...vpnKeys.all, 'dnsDomains'] as const,
  myDevices: () => [...vpnKeys.all, 'myDevices'] as const,
  policies: () => [...vpnKeys.all, 'policies'] as const,
  pathSettings: () => [...vpnKeys.all, 'pathSettings'] as const,
  reachable: () => [...vpnKeys.all, 'reachable'] as const,
  sites: () => [...vpnKeys.all, 'sites'] as const,
  appBindings: () => [...vpnKeys.all, 'appBindings'] as const,
  appBinding: (id: string) => [...vpnKeys.appBindings(), id] as const,
  appDownloads: () => [...vpnKeys.all, 'appDownloads'] as const,
};

/** Queries wait for the user and the active context, as everywhere else. */
function useReady(): boolean {
  const { user } = useAuth();
  const contextReady = useActiveContextReady();
  return !!user?.organizationId && contextReady;
}

export function useVpnSessions(query: VpnSessionQuery) {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.sessionPage(query),
    queryFn: () => fetchSessions(query),
    enabled: ready,
    placeholderData: keepPreviousData,
    // Ended sessions don't change; live ones do.
    refetchInterval: query.live === false ? false : LIVE_REFRESH_MS,
  });
}

export function useEndVpnSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: endSession,
    onSuccess: () => {
      toast.success('Session ended');
      void queryClient.invalidateQueries({ queryKey: vpnKeys.all });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't end the session")),
  });
}

export function useRegionVpns(enabled = true) {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.regions(),
    queryFn: fetchRegionVpns,
    enabled: ready && enabled,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useRegionVpn(regionId: string | undefined, enabled = true) {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.region(regionId ?? ''),
    queryFn: () => fetchRegionVpn(regionId ?? ''),
    enabled: ready && enabled && !!regionId,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useSetRegionVpn(regionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SetRegionVpn) => setRegionVpn(regionId, body),
    onSuccess: (vpn) => {
      queryClient.setQueryData(vpnKeys.region(regionId), vpn);
      void queryClient.invalidateQueries({ queryKey: vpnKeys.all });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't change the region's VPN")),
  });
}

export function useGatewayVpn(gatewayId: string | undefined) {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.gateway(gatewayId ?? ''),
    queryFn: () => fetchGatewayVpn(gatewayId ?? ''),
    enabled: ready && !!gatewayId,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useDnsDomains(enabled = true) {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.dnsDomains(),
    queryFn: fetchDnsDomains,
    enabled: ready && enabled,
  });
}

export function useSetDnsDomains() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DnsDomains) => setDnsDomains(body),
    onSuccess: () => {
      toast.success('DNS domains saved');
      void queryClient.invalidateQueries({ queryKey: vpnKeys.dnsDomains() });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't save the DNS domains")),
  });
}

export function useMyVpnDevices() {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.myDevices(),
    queryFn: fetchMyDevices,
    enabled: ready,
  });
}

export function useRevokeVpnDevice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeDevice,
    onSuccess: () => {
      toast.success('Device revoked');
      void queryClient.invalidateQueries({ queryKey: vpnKeys.all });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't revoke the device")),
  });
}

export function usePolicies(enabled = true) {
  const ready = useReady();
  return useQuery({ queryKey: vpnKeys.policies(), queryFn: fetchPolicies, enabled: ready && enabled });
}

/** Save a policy: a new one without `id`. Sessions a change no longer allows end. */
export function useSavePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: VpnPolicyInput }) => (id ? updatePolicy(id, body) : createPolicy(body)),
    onSuccess: () => {
      toast.success('Policy saved');
      void queryClient.invalidateQueries({ queryKey: vpnKeys.all });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't save the policy")),
  });
}

export function useDeletePolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deletePolicy,
    onSuccess: () => {
      toast.success('Policy deleted');
      void queryClient.invalidateQueries({ queryKey: vpnKeys.all });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't delete the policy")),
  });
}

export function usePathSettings(enabled = true) {
  const ready = useReady();
  return useQuery({ queryKey: vpnKeys.pathSettings(), queryFn: fetchPathSettings, enabled: ready && enabled });
}

export function useSetPathSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { divisionId: string | null; pathSetting: PathSetting | null }) => setPathSetting(body),
    onSuccess: (settings) => {
      queryClient.setQueryData(vpnKeys.pathSettings(), settings);
      toast.success('Connection path saved');
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't save the connection path")),
  });
}

export function useReachable() {
  const ready = useReady();
  return useQuery({ queryKey: vpnKeys.reachable(), queryFn: fetchReachable, enabled: ready, refetchInterval: LIVE_REFRESH_MS });
}

// ---- The control page (docs/VpnAccess.md, "Slice 9 design").

/** The control page follows its app this often: a toggle shows the tunnel within seconds. */
export const APP_REFRESH_MS = 3_000;

export function useVpnSites() {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.sites(),
    queryFn: fetchSites,
    enabled: ready,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

export function useBindApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bindApp,
    onSuccess: (binding) => {
      queryClient.setQueryData(vpnKeys.appBinding(binding.id), binding);
    },
  });
}

export function useAppBinding(id: string | undefined) {
  return useQuery({
    queryKey: vpnKeys.appBinding(id ?? ''),
    queryFn: () => fetchAppBinding(id ?? ''),
    enabled: !!id,
    refetchInterval: APP_REFRESH_MS,
  });
}

/** The installers the caller's channel offers. */
export function useAppDownloads() {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.appDownloads(),
    queryFn: fetchAppDownloads,
    enabled: ready,
    staleTime: 5 * 60_000,
  });
}

export function useAppBindings() {
  const ready = useReady();
  return useQuery({
    queryKey: vpnKeys.appBindings(),
    queryFn: fetchAppBindings,
    enabled: ready,
    refetchInterval: LIVE_REFRESH_MS,
  });
}

/**
 * A change to one binding. Its answer is the binding as it is now, shown at
 * once in place of the cached one, so a toggle shows what happened without
 * waiting for the next poll; then the binding is read again.
 */
function useBindingMutation<T>(
  bindingId: string,
  run: (input: T, cached: AppBinding | undefined) => Promise<AppBinding | undefined>,
  failed: string,
) {
  const queryClient = useQueryClient();
  const key = vpnKeys.appBinding(bindingId);
  return useMutation({
    mutationFn: (input: T) => run(input, queryClient.getQueryData<AppBinding>(key)),
    onSuccess: (answer) => {
      if (answer) queryClient.setQueryData(key, answer);
      void queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err) => toast.error(apiErrorMessage(err, failed)),
  });
}

export function useAcknowledgeApp(bindingId: string) {
  return useBindingMutation(bindingId, () => acknowledgeApp(bindingId), "Couldn't record that");
}

export function useTurnSiteOn(bindingId: string) {
  return useBindingMutation(bindingId, (regionId: string) => turnSiteOn(bindingId, regionId), "Couldn't turn the site on");
}

export function useTurnSiteOff(bindingId: string) {
  return useBindingMutation(
    bindingId,
    // Off answers with nothing: the binding is the cached one, without the session.
    async (sessionId: string, cached) => {
      await turnSiteOff(bindingId, sessionId);
      return cached && { ...cached, sessions: cached.sessions.filter((s) => s.id !== sessionId) };
    },
    "Couldn't turn the site off",
  );
}

export function useExtendSite(bindingId: string) {
  return useBindingMutation(
    bindingId,
    ({ sessionId, minutes }: { sessionId: string; minutes: number }) => extendSite(bindingId, sessionId, minutes),
    "Couldn't extend the session",
  );
}

export function useDisconnectApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disconnectApp,
    onSuccess: () => {
      toast.success('Disconnected');
      void queryClient.invalidateQueries({ queryKey: vpnKeys.all });
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't disconnect the app")),
  });
}
